<template>
  <ElPopover
    :visible="visible"
    :placement="placement"
    :width="width"
    :trigger="trigger"
    :title="title"
    :content="content"
    :disabled="disabled"
    :offset="offset"
    :show-arrow="showArrow"
    :popper-class="popperClass"
    :popper-style="popperStyle"
    :teleported="teleported"
    :persistent="persistent"
    @show="handleShow"
    @hide="handleHide"
    @before-show="handleBeforeShow"
    @before-hide="handleBeforeHide"
  >
    <template #reference>
      <slot name="reference">
        <ElButton v-if="!$slots.reference">{{ referenceText || '触发器' }}</ElButton>
      </slot>
    </template>

    <template #default>
      <slot>
        <div v-if="content" class="popover-content">
          {{ content }}
        </div>
      </slot>
    </template>

    <template v-if="$slots.header" #header>
      <slot name="header"></slot>
    </template>
  </ElPopover>
</template>

<script setup lang="ts">
/**
 * Popover 气泡卡片组件
 * 基于 Element Plus Popover 组件封装
 * @author AI Assistant
 * @date 2024-01-01
 */

import { ref, watch } from 'vue'
import type { Placement } from '@element-plus/components/popper'

// 接口定义
interface Props {
  /** 是否显示 */
  visible?: boolean
  /** 显示位置 */
  placement?: Placement
  /** 宽度 */
  width?: string | number
  /** 触发方式 */
  trigger?: 'click' | 'focus' | 'hover' | 'manual'
  /** 标题 */
  title?: string
  /** 内容 */
  content?: string
  /** 是否禁用 */
  disabled?: boolean
  /** 偏移量 */
  offset?: number
  /** 是否显示箭头 */
  showArrow?: boolean
  /** 弹出框类名 */
  popperClass?: string
  /** 弹出框样式 */
  popperStyle?: Record<string, any>
  /** 是否使用 teleport */
  teleported?: boolean
  /** 是否持久化 */
  persistent?: boolean
  /** 触发器文本（当没有插槽时显示） */
  referenceText?: string
}

interface Emits {
  /** 显示时触发 */
  show: []
  /** 隐藏时触发 */
  hide: []
  /** 显示前触发 */
  beforeShow: []
  /** 隐藏前触发 */
  beforeHide: []
  /** 可见性变化 */
  'update:visible': [visible: boolean]
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  visible: false,
  placement: 'bottom',
  width: 150,
  trigger: 'hover',
  title: '',
  content: '',
  disabled: false,
  offset: 12,
  showArrow: true,
  popperClass: '',
  teleported: true,
  persistent: false,
  referenceText: ''
})

// 定义 emits
const emit = defineEmits<Emits>()

// 内部可见状态
const internalVisible = ref(props.visible)

// 监听外部 visible 变化
watch(
  () => props.visible,
  (newVal) => {
    internalVisible.value = newVal
  },
  { immediate: true }
)

// 监听内部 visible 变化
watch(
  internalVisible,
  (newVal) => {
    emit('update:visible', newVal)
  }
)

/**
 * 显示事件处理
 */
const handleShow = (): void => {
  internalVisible.value = true
  emit('show')
}

/**
 * 隐藏事件处理
 */
const handleHide = (): void => {
  internalVisible.value = false
  emit('hide')
}

/**
 * 显示前事件处理
 */
const handleBeforeShow = (): void => {
  emit('beforeShow')
}

/**
 * 隐藏前事件处理
 */
const handleBeforeHide = (): void => {
  emit('beforeHide')
}

/**
 * 手动显示
 */
const show = (): void => {
  internalVisible.value = true
}

/**
 * 手动隐藏
 */
const hide = (): void => {
  internalVisible.value = false
}

/**
 * 切换显示状态
 */
const toggle = (): void => {
  internalVisible.value = !internalVisible.value
}

// 暴露方法
defineExpose({
  show,
  hide,
  toggle
})
</script>

<style scoped>
.popover-content {
  padding: 8px 0;
  line-height: 1.5;
  color: var(--el-text-color-primary);
  font-size: 14px;
}

/* 自定义样式变量 */
:deep(.el-popover) {
  --el-popover-border-radius: 6px;
  --el-popover-padding: 12px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  :deep(.el-popover) {
    max-width: 90vw;
    word-break: break-all;
  }
}
</style>
